import { motion } from "framer-motion";
import { Home, Utensils, PartyPopper, Waves, ArrowRight } from "lucide-react";
import { Reveal, MaskLinesInView } from "./motion";
import { useSmoothScroll } from "@/lib/smooth-scroll";

const B = "https://customer-assets-lxgj4vgw.emergentagent.net/job_montebello-lodge/artifacts";
const A = "https://customer-assets.emergentagent.com/job_montebello-lodge/artifacts";

const SERVICES = [
  {
    n: "01",
    icon: Home,
    title: "Alojamento Local",
    desc: "A casa inteira para si — quartos clássicos, salas amplas e a floresta privada. Reserva por noites.",
    status: "Disponível",
    img: `${A}/ri9bhuvk_image.png`,
    cta: "Reservar estadia",
    action: { type: "tab", tab: "alojamento" },
    accent: "forest",
  },
  {
    n: "02",
    icon: Utensils,
    title: "Casa Montebello | Eventos privados",
    desc: "Refeições em ambiente único, para grupos ou celebrações. Reserva de mesa por dia e turno.",
    status: "Disponível",
    img: `${B}/63699825d9548ec3_image.png`,
    cta: "Reservar mesa",
    action: { type: "tab", tab: "gastronomia" },
    accent: "terracotta",
  },
  {
    n: "03",
    icon: PartyPopper,
    title: "Casa Montebello | Eventos privados",
    desc: "Um palco único entre as árvores, com acústica natural e o ribeiro aos pés. Para casamentos, celebrações e eventos privados.",
    status: "Brevemente",
    img: `${B}/a63f7f80bb03a221_image.png`,
    cta: "Pedir informação",
    action: { type: "scroll", target: "#contactos" },
    accent: "forest",
  },
  {
    n: "04",
    icon: Waves,
    title: "Piscina",
    desc: "Espaço de lazer e piscina para arrendamento — a preparar para receber os dias mais quentes.",
    status: "Brevemente",
    img: `${B}/493a7ace4e5eb00b_image.png`,
    cta: "Pedir informação",
    action: { type: "scroll", target: "#contactos" },
    accent: "terracotta",
  },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Servicos() {
  const { scrollTo } = useSmoothScroll();

  const handle = (action) => {
    if (action.type === "tab") {
      window.dispatchEvent(new CustomEvent("cm:booking-tab", { detail: action.tab }));
      scrollTo("#reservar");
    } else {
      scrollTo(action.target);
    }
  };

  return (
    <section id="servicos" data-testid="servicos-section" className="relative py-28 lg:py-40 bg-forest text-cream overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 lg:mb-20">
          <div className="max-w-2xl">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px w-14 bg-cream/40" />
                <p className="overline text-cream/70">Serviços</p>
              </div>
            </Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[4.2rem] leading-[1.02] tracking-tight font-light">
              <MaskLinesInView lines={["Uma casa,", <span key="i" className="italic">várias experiências.</span>]} />
            </h2>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-md text-cream/70 leading-relaxed">
              Dentro da Casa Montebello convivem vários espaços, cada um para alugar de forma independente — do descanso à celebração.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {SERVICES.map(({ icon: Icon, ...s }, i) => (
            <motion.article
              key={s.title}
              data-testid={`service-card-${s.action.type === "tab" ? s.action.tab : s.title.toLowerCase().replace(/\s+/g, "-")}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.7, ease: EASE, delay: (i % 2) * 0.08 }}
              className="group relative overflow-hidden rounded-sm bg-[#26361f] border border-cream/10"
            >
              <div className="relative h-56 lg:h-64 overflow-hidden">
                <img src={s.img} alt={s.title} className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1c2916] via-transparent to-transparent" />
                <span className={`absolute top-4 right-4 px-3 h-7 inline-flex items-center rounded-full text-[0.62rem] tracking-[0.16em] uppercase font-medium ${s.status === "Disponível" ? "bg-cream text-forest" : "bg-black/40 text-cream border border-cream/40"}`}>
                  {s.status}
                </span>
                {s.status === "Brevemente" && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-black/55 backdrop-blur-sm border border-cream/30 px-6 py-3 rounded-sm text-center">
                      <p className="font-display italic text-cream text-2xl lg:text-3xl leading-tight">Brevemente</p>
                      <p className="mt-1 text-cream/80 text-[0.65rem] tracking-[0.22em] uppercase">Em preparação</p>
                    </div>
                  </div>
                )}
                <div className="absolute bottom-4 left-5 flex items-center gap-3">
                  <Icon size={22} strokeWidth={1.5} className="text-cream" />
                  <span className="font-display text-cream/40 text-3xl">{s.n}</span>
                </div>
              </div>

              <div className="p-6 lg:p-8">
                <h3 className="font-display text-2xl lg:text-3xl">{s.title}</h3>
                <p className="mt-3 text-cream/70 text-[0.95rem] leading-relaxed">{s.desc}</p>
                <button
                  data-testid={`service-cta-${i}`}
                  onClick={() => handle(s.action)}
                  className={`mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full text-[0.75rem] tracking-[0.18em] uppercase font-medium transition-all ${s.accent === "terracotta" ? "bg-terracotta text-cream hover:brightness-95" : "bg-cream text-forest hover:bg-white"}`}
                >
                  {s.cta}
                  <ArrowRight size={14} strokeWidth={1.75} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
